import { PageShell } from '@/components/PageShell';
import RockDocRenderer from '@/components/docs/RockDocRenderer';
import IdWithCopy from '@/components/IdWithCopy';
import { JsonViewer } from '@/components/JsonViewer';
import { MetadataItem } from '@/components/MetadataItem';
import { TabNav } from '@/components/TabNav';
import { ConsiderationCriticalityBadge } from '@/components/badges/ConsiderationCriticalityBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowClick } from '@/lib/navigation';
import { considerationDocState, considerationTypeState, taskTypesForConsiderationTypeState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import type { ConsiderationType, ConsiderationTypeId } from '@rock/types';
import { orderBy } from 'lodash';
import { ArrowLeft } from 'lucide-react';
import type { FC } from 'react';
import { useMemo } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { TaskCadenceCells, TaskCadenceHeads } from '../tasks/TaskCadenceCells';
import { TaskTypeDetailsPage } from '../tasks/TaskTypeDetailsPage';
import { ConsiderationTypeResultsPage } from './results/ConsiderationTypeResultsPage';
import { AppliesToCard } from './applies-to/AppliesToCard';
import { ConsiderationTypeHomesTab } from './ConsiderationTypeHomesTab';

export const ConsiderationTypeDetailsPage: FC = () => {
    const { typeId } = useParams<{ typeId?: ConsiderationTypeId }>();
    const [considerationType] = useLoadableAtomValue(considerationTypeState(typeId));

    if (considerationType) {
        return <ConsiderationTypeDetailsContent considerationType={considerationType} />;
    }
};

const ConsiderationTypeDetailsContent: FC<{ considerationType: ConsiderationType }> = ({ considerationType }) => {
    const navigate = useNavigate();
    const typeId = considerationType.id;

    const [taskTypes] = useLoadableAtomValue(
        useMemo(() => taskTypesForConsiderationTypeState(typeId), [typeId]),
        [],
    );
    const taskCount = taskTypes?.length ?? 0;

    return (
        <PageShell width="narrow">
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(-1)}
                            aria-label="Back to considerations"
                            className="mt-1"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div>
                            <h1 className="text-3xl font-semibold">{considerationType.title}</h1>
                            <IdWithCopy id={typeId} variant="muted" className="mt-1 text-sm" showLabel />
                        </div>
                    </div>
                </div>
            </div>

            <div className="mb-6">
                <TabNav
                    tabs={[
                        { label: 'Details', to: `/content/considerations/${typeId}/details` },
                        { label: 'Tasks', to: `/content/considerations/${typeId}/tasks`, count: taskCount },
                        { label: 'Results', to: `/content/considerations/${typeId}/results` },
                        { label: 'Homes', to: `/content/considerations/${typeId}/homes` },
                    ]}
                />
            </div>

            <Routes>
                <Route index element={<Navigate replace to="./details" />} />
                <Route path="details" element={<ConsiderationTypeDetailsTab considerationType={considerationType} />} />
                <Route path="tasks" element={<ConsiderationTypeTasksTab considerationType={considerationType} />} />
                <Route
                    path="tasks/:taskTypeId/*"
                    element={<TaskTypeDetailsPage considerationType={considerationType} />}
                />
                <Route
                    path="results/*"
                    element={<ConsiderationTypeResultsPage considerationType={considerationType} />}
                />
                <Route path="homes" element={<ConsiderationTypeHomesTab considerationType={considerationType} />} />
            </Routes>
        </PageShell>
    );
};

const ConsiderationTypeDetailsTab: FC<{ considerationType: ConsiderationType }> = ({ considerationType }) => {
    const considerationDocId = considerationType?.considerationDocId;
    const [rockDoc, rockDocLoadable] = useLoadableAtomValue(
        useMemo(() => considerationDocState(considerationDocId), [considerationDocId]),
    );
    const rockDocError = rockDocLoadable.state === 'hasError' ? rockDocLoadable.error : undefined;
    const isRockDocLoading = considerationDocId ? rockDocLoadable.state === 'loading' : false;

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader className="border-b-1 pb-3">
                    <CardTitle className="text-xl">Metadata</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid gap-5 md:grid-cols-2">
                        <MetadataItem label="Title" value={considerationType.title} />
                        <MetadataItem label="Space" value={considerationType.spaceId || '-'} />
                        <MetadataItem label="Model Number" value={considerationType.modelNumber || '-'} />
                        <MetadataItem
                            label="Default Criticality"
                            value={<ConsiderationCriticalityBadge criticality={considerationType.defaultCriticality} />}
                        />
                    </div>
                </CardContent>
            </Card>

            <AppliesToCard type={considerationType} />

            <Card>
                <CardHeader className="flex flex-col gap-2 border-b-1 pb-3">
                    <CardTitle className="text-xl">Content</CardTitle>
                    {!considerationDocId ? (
                        <div className="section-description">This type is not linked to a Doc yet.</div>
                    ) : null}
                    {isRockDocLoading ? <div className="section-description">Loading Doc…</div> : null}
                    {rockDocError ? (
                        <div className="text-sm text-destructive">
                            Unable to load Doc:{' '}
                            {rockDocError instanceof Error ? rockDocError.message : `${rockDocError}`}
                        </div>
                    ) : null}
                </CardHeader>
                <CardContent>
                    {rockDoc ? (
                        <div className="space-y-4">
                            <div className="space-y-1">
                                <h3 className="text-lg font-medium text-card-foreground">{rockDoc.title}</h3>
                                <div className="section-description">
                                    Last synced {new Date(rockDoc.updatedAt).toLocaleString()}
                                </div>
                            </div>
                            <RockDocRenderer blocks={rockDoc.blocks} />
                        </div>
                    ) : !considerationDocId ? (
                        <div className="section-description">
                            Link a Doc in the metadata section to display its content here.
                        </div>
                    ) : isRockDocLoading ? null : (
                        <div className="section-description">No Doc content available for this type.</div>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Consideration Type Object</CardTitle>
                    <CardDescription>The complete consideration type payload stored in Firestore.</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="max-h-[480px] overflow-auto rounded-md bg-muted p-4">
                        <JsonViewer data={considerationType} />
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

const ConsiderationTypeTasksTab: FC<{ considerationType: ConsiderationType }> = ({ considerationType }) => {
    const typeId = considerationType.id;
    const onRowClick = useRowClick();
    const [taskTypes, taskTypesLoadable] = useLoadableAtomValue(
        useMemo(() => taskTypesForConsiderationTypeState(typeId), [typeId]),
        [],
    );
    const sortedTaskTypes = useMemo(() => orderBy(taskTypes, [(t) => t.title.toLowerCase()]), [taskTypes]);

    return (
        <div className="space-y-6">
            {taskTypesLoadable.state === 'loading' ? (
                <Card>
                    <CardContent className="p-6">
                        <div className="text-muted-foreground">Loading tasks…</div>
                    </CardContent>
                </Card>
            ) : taskTypesLoadable.state === 'hasError' ? (
                <Card>
                    <CardContent className="p-6">
                        <div className="text-destructive">
                            Error loading tasks:{' '}
                            {taskTypesLoadable.error instanceof Error
                                ? taskTypesLoadable.error.message
                                : `${taskTypesLoadable.error}`}
                        </div>
                    </CardContent>
                </Card>
            ) : sortedTaskTypes.length === 0 ? (
                <Card>
                    <CardHeader>
                        <CardTitle>No tasks found</CardTitle>
                        <CardDescription>
                            Tasks will appear here once they are synced from Notion for this consideration type.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="section-description">
                            Create a task doc in Notion related to this consideration type, then sync it to create a
                            task type.
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <CardHeader className="border-b-1 pb-3">
                        <CardTitle className="text-xl">Tasks</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="px-4 pb-4">
                            <Table className="not-prose">
                                <TableHeader className="bg-secondary">
                                    <TableRow>
                                        <TableHead className="min-w-[200px]">Title</TableHead>
                                        <TaskCadenceHeads />
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sortedTaskTypes.map((taskType) => {
                                        const taskPath = `/content/considerations/${typeId}/tasks/${encodeURIComponent(taskType.id)}/details`;
                                        return (
                                            <TableRow
                                                key={taskType.id}
                                                className="cursor-pointer transition-colors hover:bg-muted/50"
                                                onClick={onRowClick(taskPath)}
                                            >
                                                <TableCell className="font-medium">{taskType.title}</TableCell>
                                                <TaskCadenceCells taskType={taskType} />
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};
